import type { FeedProposalView, InstanceDetail } from "@keydials/api-client";

/**
 * The changes a feed is waiting on you for (KEY-361).
 *
 * **Why there is a queue at all.** You wrote the wire against their dial *as it was*, and the other
 * organisation can change what it says at any time without asking you. So an incoming change is shown
 * before it lands, unless you opted that particular wire in to applying by itself.
 *
 * **What a blocked one means.** Your own status gate refused it — a field your rule requires is still
 * empty. It is not an error and there is nothing to click: it lands by itself the moment you fill that
 * field in. The gate is your rule and the feed is somebody else's input, so the rule wins.
 *
 * Their status is deliberately not shown. It is drawn from their palette, which means nothing on your
 * dial — that mismatch is the entire reason a wire carries a translation. What you decide on is what it
 * would do to *your* node.
 */
export function FeedProposalsPanel({
  proposals,
  detail,
  busy,
  onDecide,
}: {
  proposals: FeedProposalView[];
  detail: InstanceDetail;
  busy: boolean;
  onDecide: (proposal: FeedProposalView, accept: boolean) => void;
}) {
  if (proposals.length === 0) {
    return null;
  }

  /** A status key in your own vocabulary, from whichever palette that node draws on. */
  const statusName = (proposal: FeedProposalView, key: string | null): string => {
    if (!key) return "—";
    if (proposal.targetDimensionKey) {
      const dimension = (detail.dimensions ?? []).find((d) => d.key === proposal.targetDimensionKey);
      return dimension?.palette.statuses.find((s) => s.key === key)?.name ?? key;
    }

    const segment = detail.sections
      .find((s) => s.key === proposal.targetSectionKey)
      ?.segments.find((g) => g.key === proposal.targetSegmentKey);
    const palette = segment?.palette ? detail.palettes.find((p) => p.name === segment.palette) : undefined;
    return (palette?.statuses ?? detail.statusSet.statuses).find((s) => s.key === key)?.name ?? key;
  };

  const describe = (proposal: FeedProposalView): string =>
    proposal.targetAttributeKey
      ? `set “${proposal.proposedValue}”${proposal.currentValue ? ` (now “${proposal.currentValue}”)` : ""}`
      : `${statusName(proposal, proposal.currentStatusKey)} → ${statusName(proposal, proposal.proposedStatusKey)}`;

  return (
    <>
      <h3
        style={{
          fontSize: 12.5,
          fontWeight: 700,
          letterSpacing: 0.3,
          textTransform: "uppercase",
          color: "var(--text-muted)",
          margin: "0 0 8px",
        }}
      >
        Waiting for you · {proposals.length} {proposals.length === 1 ? "change" : "changes"}
      </h3>

      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 18 }}>
        {proposals.map((proposal) => (
          <div
            key={proposal.id}
            style={{
              border: "var(--border-w) solid var(--border)",
              borderRadius: "var(--radius-sm)",
              padding: "10px 12px",
              display: "flex",
              flexDirection: "column",
              gap: 6,
            }}
          >
            <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
              <span style={{ fontWeight: 700, fontSize: 13.5, color: "var(--text)" }}>
                {proposal.fromOrganisationName}
              </span>
              <span style={{ fontSize: 12.5, color: "var(--text-faint)" }}>
                wants to change <strong style={{ color: "var(--text)" }}>{proposal.targetLabel}</strong>
              </span>
            </div>

            <div style={{ fontSize: 13, color: "var(--text)" }}>{describe(proposal)}</div>

            <div style={{ fontSize: 11.5, color: "var(--text-faint)" }}>
              from “{proposal.fromDialName}” · {proposal.fromNodePath} · via “{proposal.mappingName}”
            </div>

            {proposal.state === "Blocked" ? (
              // Nothing to click. Saying which field, and that it lands by itself, is the whole answer.
              <div style={{ fontSize: 12, color: "var(--st-overdue)" }}>
                Held by your own rule until <strong>{proposal.blockedFieldLabel}</strong> is filled in on
                that action point. It will land on its own once it is.
              </div>
            ) : (
              <div style={{ display: "flex", gap: 8 }}>
                <button className="kd-btn-primary" disabled={busy} onClick={() => onDecide(proposal, true)}>
                  Accept
                </button>
                <button className="kd-btn" disabled={busy} onClick={() => onDecide(proposal, false)}>
                  Turn down
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );
}
